import { ConvertFileToPdfSlideover } from "@/components/features/matter/file-repo/ConvertFileToPdfSlideover";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { EditDocxFirmFileModal } from "@/components/features/settings/firm/EditDocxFirmFileModal";
import { EditPdfFirmFileModal } from "@/components/features/settings/firm/EditPdfFirmFileModal";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import {
  Button,
  Form,
  FormSubmitButton,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateFirmFile,
  useGenerateFirmFilePdf,
  useUpdateFirmFile,
} from "@/lib/data/react-query";
import { FirmFileSchema } from "@/lib/data/schemas/firm/FirmSchema";
import { acceptedFileMimeTypes } from "@/lib/data/static/fileStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { FirmFile } from "@/lib/types";
import {
  firmFileDisplayNameExistsInFolder,
  getFirmFileBaseName,
  getFirmFileDisplayName,
  getFirmFileDisplayNamesInFolder,
  getFirmFileExtension,
} from "@/lib/utils/firmFile";
import {
  ensureFileNameHasExtension,
  getFileNameParts,
  getFileDocumentFormat,
} from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import type { z } from "zod";

const firmFileFormSchema = FirmFileSchema.pick({
  name: true,
  description: true,
});

type FirmFileFormData = z.infer<typeof firmFileFormSchema>;

const defaultValues = {
  name: "",
  description: "",
};

interface AddOrEditFirmFileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  activeFile?: FirmFile;
  currentFolderId?: string;
  isFolder?: boolean;
  autoOpenUpload?: boolean;
  prefillFile?: { file: File; url: string };
  prefillValues?: Partial<FirmFileFormData>;
  existingFiles?: FirmFile[];
  overlayClassName?: string;
  className?: string;
}

export function AddOrEditFirmFileModal({
  open,
  setOpen,
  editing = false,
  activeFile,
  currentFolderId = "",
  isFolder = false,
  autoOpenUpload,
  prefillFile,
  prefillValues,
  existingFiles = [],
  overlayClassName,
  className,
}: AddOrEditFirmFileModalProps) {
  const createFirmFile = useCreateFirmFile();
  const generateFirmFilePdf = useGenerateFirmFilePdf();
  const updateFirmFile = useUpdateFirmFile();
  const isFormLoading =
    createFirmFile.isPending ||
    updateFirmFile.isPending ||
    generateFirmFilePdf.isPending;
  const { hasActionPermission } = useUser();
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDocumentEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.update,
  );

  const isEditingFolder = editing ? Boolean(activeFile?.isFolder) : isFolder;
  const isActiveDocx =
    !!activeFile &&
    getFileDocumentFormat({
      ...activeFile,
      name: getFirmFileDisplayName(activeFile),
    }) === "docx";
  const isActivePdf =
    !!activeFile &&
    getFileDocumentFormat({
      ...activeFile,
      name: getFirmFileDisplayName(activeFile),
    }) === "pdf";

  const [file, setFile] = useState<{ file: File; url: string } | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoOpenUploadHandledRef = useRef(false);
  const [isDocxEditorOpen, setIsDocxEditorOpen] = useState(false);
  const [isPdfEditorOpen, setIsPdfEditorOpen] = useState(false);
  const [isConvertToPdfOpen, setIsConvertToPdfOpen] = useState(false);

  const form = useForm<FirmFileFormData>({
    resolver: zodResolver(firmFileFormSchema),
    values: editing
      ? {
          name: getFirmFileBaseName(
            activeFile ?? { name: "", isFolder: false },
          ),
          description: activeFile?.description ?? "",
        }
      : {
          ...defaultValues,
          ...(prefillValues ?? {}),
        },
    mode: "onSubmit",
  });

  const hasPrefill =
    !editing &&
    (Boolean(prefillFile) ||
      Boolean(prefillValues?.name) ||
      Boolean(prefillValues?.description));

  useEffect(() => {
    if (!open) {
      setFile(undefined);
      form.reset();
      setIsDocxEditorOpen(false);
      setIsPdfEditorOpen(false);
      setIsConvertToPdfOpen(false);
    }
  }, [open, form]);

  useEffect(() => {
    if (!open || editing || !prefillFile) return;
    setFile(prefillFile);
  }, [editing, open, prefillFile]);

  const targetFolderId = editing
    ? (activeFile?.idLocation ?? "")
    : currentFolderId;

  const hasNameConflict = (candidate: string, excludeId?: string) =>
    firmFileDisplayNameExistsInFolder(
      existingFiles,
      targetFolderId,
      candidate,
      excludeId,
    );

  const createFile = async (
    data: FirmFileFormData,
    uploadedFile?: { file: File; url: string },
  ) => {
    const currentFile = uploadedFile ?? file;
    if (!currentFile) {
      toast.error("Please upload a file");
      return;
    }

    const extension = getFileNameParts(currentFile.file.name).fileExtension;
    const displayName = ensureFileNameHasExtension(data.name, extension);
    await createFirmFile.mutateAsync({
      name: displayName,
      description: data.description,
      idLocation: currentFolderId,
      isFolder: false,
      file: currentFile.file,
    });
    setOpen(false);
  };

  const createFolder = async (data: FirmFileFormData) => {
    const folderName = data.name.trim();
    if (hasNameConflict(folderName)) {
      form.setError("name", {
        type: "manual",
        message: "A folder with this name already exists",
      });
      return;
    }

    await createFirmFile.mutateAsync({
      name: folderName,
      description: data.description,
      idLocation: currentFolderId,
      isFolder: true,
      presignedUrlKey: "",
    });
    setOpen(false);
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const first = files[0];
    const parts = getFileNameParts(first.name);
    if (!form.getValues("name")) {
      form.setValue("name", parts.fileName, { shouldDirty: true });
    }
    const url = URL.createObjectURL(first);
    const uploadedFile = { file: first, url };
    setFile(uploadedFile);
    if (autoOpenUpload && !editing && !isEditingFolder) {
      void form
        .handleSubmit((data) => createFile(data, uploadedFile))()
        .catch(() => undefined);
    }
  };

  const handleRemoveFileUpload = () => {
    setFile(undefined);
  };

  const onSubmit = async (data: FirmFileFormData) => {
    if (editing) {
      if (!activeFile) return;

      const extension = getFirmFileExtension(activeFile);
      const displayName = isEditingFolder
        ? data.name.trim()
        : ensureFileNameHasExtension(data.name, extension);

      if (hasNameConflict(displayName, activeFile.id)) {
        form.setError("name", {
          type: "manual",
          message: "An item with this name already exists",
        });
        return;
      }

      await updateFirmFile.mutateAsync({
        id: activeFile.id,
        revision: activeFile.revision,
        dateModified: activeFile.dateModified,
        name: displayName,
        description: data.description,
      });
      setOpen(false);
      return;
    }

    if (isEditingFolder) {
      await createFolder(data);
      return;
    }

    await createFile(data);
  };

  useEffect(() => {
    if (!open || editing || isEditingFolder) {
      autoOpenUploadHandledRef.current = false;
      return;
    }
    if (!autoOpenUpload) {
      autoOpenUploadHandledRef.current = false;
      return;
    }
    if (file || autoOpenUploadHandledRef.current) return;

    autoOpenUploadHandledRef.current = true;
    const timeoutId = window.setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);

    return () => {
      window.clearTimeout(timeoutId);
      autoOpenUploadHandledRef.current = false;
    };
  }, [autoOpenUpload, editing, file, isEditingFolder, open]);

  const canEditDocx =
    editing &&
    !isEditingFolder &&
    isActiveDocx &&
    hasUpdatePermission &&
    hasDocumentEditPermission;
  const canEditPdf =
    editing &&
    !isEditingFolder &&
    isActivePdf &&
    hasUpdatePermission &&
    hasDocumentEditPermission;
  const canConvertToPdf =
    editing && !isEditingFolder && isActiveDocx && hasCreatePermission;

  const extensionPreview = editing
    ? activeFile && !isEditingFolder
      ? getFirmFileExtension(activeFile)
      : ""
    : file
      ? getFileNameParts(file.file.name).fileExtension
      : "";

  const FIRM_FILE_FORM_ID = "add-or-edit-firm-file-form";
  const submitFormId = FIRM_FILE_FORM_ID;
  const title = editing
    ? `Edit ${isEditingFolder ? "folder" : "file"}`
    : autoOpenUpload && !isEditingFolder
      ? "Upload file"
      : `New ${isEditingFolder ? "folder" : "file"}`;
  const description = editing
    ? `Update the ${isEditingFolder ? "folder" : "file"} details.`
    : isEditingFolder
      ? "Create a new folder."
      : "Upload a firm file and give it a name.";
  const footerActions = (
    <div className="flex grow justify-end gap-x-2">
      <FormCancelButton setOpen={setOpen} isDirty={form.formState.isDirty} />
      {canConvertToPdf && (
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setIsConvertToPdfOpen(true);
          }}
          disabled={isFormLoading}
        >
          Convert to PDF
        </Button>
      )}
      <FormSubmitButton
        loading={createFirmFile.isPending || updateFirmFile.isPending}
        disabled={editing && !hasUpdatePermission}
        overrideDisabled={hasPrefill}
        submitFormId={submitFormId}
      />
    </div>
  );
  const firmFileForm = (
    <form
      id={FIRM_FILE_FORM_ID}
      className="flex min-h-0 grow flex-col overflow-hidden"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <div className="overflow-y-auto">
        <fieldset
          disabled={editing ? !hasUpdatePermission : false}
          className={slideoverFieldsetClassName}
        >
          <TextFormField register={form.register("name")} label="Name*" />
          <div className="self-end">
            <Typography variant="subHeading" className="mb-3">
              {extensionPreview ? `.${extensionPreview}` : ""}
            </Typography>
          </div>
          <div className="col-span-2">
            <TextAreaFormField
              register={form.register("description")}
              label="Description"
            />
          </div>

          {!editing && !isEditingFolder && (
            <>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload file
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={(e) => {
                  handleFileUpload(e.target.files);
                }}
                accept={acceptedFileMimeTypes.join(",")}
              />
              <div />
              {file && (
                <div className="col-span-full">
                  <FileViewer
                    file={{ file: file.file, url: file.url }}
                    fileType="firm-file"
                    showDownload={false}
                    handleRemoveFileUpload={handleRemoveFileUpload}
                    imageClassName="max-h-[400px] w-auto"
                  />
                </div>
              )}
            </>
          )}

          {editing && activeFile && !isEditingFolder && (
            <div className="col-span-full">
              <FileViewer
                file={{
                  fileName: getFirmFileDisplayName(activeFile),
                  fileId: activeFile.id,
                  presignedURLKey: activeFile.presignedUrlKey || "",
                }}
                fileType="firm-file"
                imageClassName="max-h-[400px] w-auto"
                onEdit={
                  canEditDocx || canEditPdf
                    ? () => {
                        if (canEditDocx) {
                          setIsDocxEditorOpen(true);
                          return;
                        }
                        if (canEditPdf) {
                          setIsPdfEditorOpen(true);
                        }
                      }
                    : undefined
                }
              />
            </div>
          )}
        </fieldset>
      </div>
    </form>
  );
  const supportingSlideovers = (
    <>
      {activeFile && isActiveDocx && !isEditingFolder && (
        <EditDocxFirmFileModal
          open={isDocxEditorOpen}
          setOpen={setIsDocxEditorOpen}
          sourceFile={activeFile}
          onSaved={() => {
            setOpen(false);
          }}
        />
      )}
      {activeFile && isActivePdf && !isEditingFolder && (
        <EditPdfFirmFileModal
          open={isPdfEditorOpen}
          setOpen={setIsPdfEditorOpen}
          sourceFile={activeFile}
          onSaved={() => {
            setOpen(false);
          }}
        />
      )}
      {activeFile && !isEditingFolder && (
        <ConvertFileToPdfSlideover
          open={isConvertToPdfOpen}
          setOpen={setIsConvertToPdfOpen}
          sourceFileName={getFirmFileDisplayName(activeFile)}
          existingFileNames={getFirmFileDisplayNamesInFolder(
            existingFiles,
            activeFile.idLocation || "",
          )}
          loading={generateFirmFilePdf.isPending}
          overlayClassName="z-[102] bg-black/50 backdrop-blur-md"
          className="z-[103]"
          onConvert={async (firmFileName) => {
            await generateFirmFilePdf.mutateAsync({
              id: activeFile.id,
              name: firmFileName,
            });
            setOpen(false);
          }}
        />
      )}
    </>
  );

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="2xl"
          overlayClassName={overlayClassName}
          className={`w-[calc(100vw-2rem)] max-w-4xl ${className ?? ""}`}
          footer={
            <div className="flex w-full flex-wrap justify-end gap-2">
              {footerActions}
            </div>
          }
        >
          {firmFileForm}
        </AppModal>
      </Form>
      {supportingSlideovers}
    </>
  );
}
